import BackButton from '../components/BackButton';
import { useState, useEffect } from 'react';
import { useApp } from '../App';
import api from '../api';
import { useI18n } from '../i18n/index.jsx';

export default function AdminPayouts() {
  const { isAdmin } = useApp();
  const { t } = useI18n();
  const [payouts, setPayouts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    if (isAdmin) loadData();
  }, [isAdmin]);

  async function loadData() {
    setLoading(true);
    try {
      const [payoutsRes, statsRes] = await Promise.all([
        api.get('/payouts/admin/list'),
        api.get('/payouts/admin/stats'),
      ]);
      setPayouts(payoutsRes.data || []);
      setStats(statsRes.data);
    } catch (e) {
      setError(e.userMessage);
    } finally {
      setLoading(false);
    }
  }

  async function handleAction(id, action) {
    setProcessing(id);
    try {
      if (action === 'approve') {
        await api.post('/payouts/admin/approve', { payout_id: id });
      } else if (action === 'send') {
        await api.post(`/payouts/admin/send/${id}`);
      } else if (action === 'reject') {
        await api.post(`/payouts/admin/reject/${id}`);
      }
      await loadData();
    } catch (e) {
      setError(e.userMessage);
    } finally {
      setProcessing(null);
    }
  }

  if (!isAdmin) return <div className="page"><BackButton /><h2>⛔ Доступ запрещён</h2></div>;

  const statusColor = (s) => ({
    requested: '#ffc107',
    approved: '#6c5ce7',
    sent: '#00cec9',
    rejected: '#ff6b6b',
  }[s] || '#888');

  const statusLabel = (s) => ({
    requested: '⏳ Ожидает',
    approved: '✅ Одобрено',
    sent: '💸 Отправлено',
    rejected: '❌ Отклонено',
  }[s] || s);

  const filtered = filter === 'all' 
    ? payouts 
    : payouts.filter(p => p.status === filter);

  const formatDate = (d) => d ? new Date(d).toLocaleString('ru-RU') : '—';

  return (
    <div className="page"><BackButton />
      <h1>💸 Админ: Выводы</h1>

      {error && <div className="error-box">{error}</div>}

      {/* Stats */}
      {stats && (
        <div className="payout-stats" style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(4, 1fr)', 
          gap: 12, 
          marginBottom: 20,
          background: 'rgba(0,0,0,0.3)',
          padding: 16,
          borderRadius: 12,
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 'bold', color: '#ffc107' }}>{stats.pending_count}</div>
            <div style={{ fontSize: 12, opacity: 0.7 }}>Ожидают</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 'bold', color: '#00cec9' }}>{stats.by_status?.sent || 0}</div>
            <div style={{ fontSize: 12, opacity: 0.7 }}>Отправлено</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 'bold', color: '#6c5ce7' }}>{stats.total_commission?.toFixed(0)} PLN</div>
            <div style={{ fontSize: 12, opacity: 0.7 }}>Комиссия (6%)</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 'bold', color: '#00cec9' }}>{stats.total_sent?.toFixed(0)} PLN</div>
            <div style={{ fontSize: 12, opacity: 0.7 }}>Выплачено</div>
          </div>
        </div>
      )}

      {/* Filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {['all', 'requested', 'approved', 'sent', 'rejected'].map(f => (
          <button
            key={f}
            className={`btn ${filter === f ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setFilter(f)}
            style={{ fontSize: 13 }}
          >
            {f === 'all' ? 'Все' : statusLabel(f)} 
            {f !== 'all' && ` (${payouts.filter(p => p.status === f).length})`}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="loader">{t('common.loading')}</div>
      ) : filtered.length === 0 ? (
        <p style={{ opacity: 0.6 }}>Нет заявок на вывод</p>
      ) : (
        <div className="list">
          {filtered.map(p => (
            <div 
              key={p.id} 
              className="list-item" 
              style={{ borderLeft: `4px solid ${statusColor(p.status)}` }}
            >
              <div className="list-main">
                <div className="list-title">
                  #{p.id} — {p.user?.first_name} {p.user?.last_name}
                </div>
                <div className="list-sub">
                  🏢 {p.company?.name || '—'} • TG: {p.user?.telegram_id}
                </div>
                <div className="list-sub" style={{ marginTop: 4 }}>
                  💰 Запрос: <strong>{p.requested_amount?.toFixed(2)} PLN</strong> • 
                  Комиссия: {p.commission_amount?.toFixed(2)} PLN ({p.commission_mode}) • 
                  <span style={{ color: '#00cec9' }}>К выплате: <strong>{p.net_amount?.toFixed(2)} PLN</strong></span>
                </div>
                <div className="list-sub" style={{ fontSize: 11, opacity: 0.6 }}>
                  📅 {formatDate(p.created_at)}
                  {p.sent_at && ` • Отправлено: ${formatDate(p.sent_at)}`}
                </div>
              </div>
              <div className="list-right" style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
                <span className="status-badge" style={{ background: statusColor(p.status) }}>
                  {statusLabel(p.status)}
                </span>
                {p.status === 'requested' && (
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button 
                      className="btn btn-small" 
                      style={{ background: '#00cec9', padding: '4px 8px', fontSize: 11 }}
                      onClick={() => handleAction(p.id, 'approve')}
                      disabled={processing === p.id}
                    >
                      ✅
                    </button>
                    <button 
                      className="btn btn-small" 
                      style={{ background: '#ff6b6b', padding: '4px 8px', fontSize: 11 }}
                      onClick={() => handleAction(p.id, 'reject')}
                      disabled={processing === p.id}
                    >
                      ❌
                    </button>
                  </div>
                )}
                {p.status === 'approved' && (
                  <button 
                    className="btn btn-small" 
                    style={{ background: '#6c5ce7', padding: '4px 8px', fontSize: 11 }}
                    onClick={() => handleAction(p.id, 'send')}
                    disabled={processing === p.id}
                  >
                    💸 Отправить
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 24, display: 'flex', gap: 8 }}>
        <button className="btn btn-secondary" onClick={loadData}>🔄 Обновить</button>
      </div>
    </div>
  );
}
